import { constants } from '@ulta/core/utils/constants/constants';

const { AMEXCARD_FORMATTER, OTHERCARD_FORMATTER } = constants.CVV;
const { CREDITCARD_FORMATTER, CREDITCARD_FORMATTER_AMEX } = constants.FORMATTER_PATTERNS;

export const ccConstants = {
  CCNUMBER: {
    DISCOVERREGEX: /^6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65/,
    ULTAMATEREGEX: /^5780|9710[5-9]/,
    AMEXREGEX: /^3[47]/,
    ULTAREWARDSREGEX: /^536817/,
    VISAREGEX: /^4[0-9]/,
    MASTERCARDREGEX:
      /^(5([1-6][0-9][0-9][0-9][0-9])|22([2-9][1-9][0-9][0-9])|23([0-9][0-9][0-9][0-9])|24([0-9][0-9][0-9][0-9])|25([0-9][0-9][0-9][0-9])|26([0-9][0-9][0-9][0-9])|27([0-2]0[0-9][0-9]))/,
    EXPDATEREGEX: /^(?:(0[1-9]|1[012])[\/.][1-9]{1}[0-9]{3})$/,
    CC_EXPDATE_REGEX: /^(0[1-9]|1[0-2])\/?([0-9]{4}|[0-9]{2})$/,
    AMEXREGEXVALIDATE: /([0-9]{4}\s[0-9]{6}\s[0-9]{5}\s*)$/,
    NONAMEXREGEXVALIDATE: /([0-9]{4}\s[0-9]{4}\s[0-9]{4}\s[0-9]{4}\s*)$/,
    AMEXCVVVALIDATE: /([0-9]{4})$/,
    NONAMEXCVVVALIDATE: /([0-9]{3})$/,
    DINERSCLUBREGX: /^36/,
    JCBREGX: /^(35[2-8][0-9])/,
    UNIONPAYREGX: /^(62[0-9]{14,17})$/,
    DISCOVER: 'Discover',
    ULTAMATE: ['Ultamate Rewards Credit Card', 'Ulta Beauty Rewards Credit Card'],
    AMEX: 'AmericanExpress',
    ULTAREWARDS: ['Ultamate Rewards MasterCard', 'Ulta Beauty Rewards MasterCard'],
    VISA: 'Visa',
    MASTERCARD: 'Mastercard',
    DINERSCLUB: 'DinersClub',
    JCB: 'JCB',
    UNIONPAY: 'UnionPay',
    DEFAULTCARD: 'defaultcreditcard'
  },
  creditCardTypeMapping: {
    'Visa': '001',
    'Ultamate Rewards Credit Card': '002',
    'Ultamate Rewards MasterCard': '002',
    'Ulta Beauty Rewards MasterCard': '002',
    'Ulta Beauty Rewards Credit Card':'002',
    'Mastercard': '002',
    'AmericanExpress': '003',
    'Discover': '004',
    'JCB':'007',
    'UnionPay':'062',
    'DinersClub':'005'
  },
  FORMATTER_PATTERNS: {
    THREE_DIGIT_CVV: { pattern: '999' },
    FOUR_DIGIT_CVV: { pattern: '9999' },
    EXPDATE_FORMATTER: { pattern: '99/99' }
  }
};

const CC_TYPES = {
  AMEX: 'AmericanExpress',
  DINERSCLUB: 'DinersClub'
};

/**
 * Adds detail to card type
 * @param {object} data - Arguments
 * @param {object} data.cardType - Incoming card type
 * @returns {object} - Card type
 */
export const decorateCardType = ( data ) => {
  const { cardType = {} } = data || {};

  if(
    !cardType.type ||
    !cardType.maxCVVLength ||
    !cardType.maxNumberLength
  ){
    return cardType;
  }
  cardType.ccFormatter = cardType.type === CC_TYPES.AMEX ? CREDITCARD_FORMATTER_AMEX : CREDITCARD_FORMATTER;
  cardType.cvvFormatter = cardType.maxCVVLength === 4 ? AMEXCARD_FORMATTER : OTHERCARD_FORMATTER;

  // to account for spaces added with the input masking
  cardType.ccNumberLength =
    cardType.type === CC_TYPES.AMEX ?
      cardType.maxNumberLength + 2 :
      cardType.type === CC_TYPES.DINERSCLUB ?
        cardType.maxNumberLength + 1 :
        cardType.maxNumberLength + 3;

  return cardType;
};

/**
 * Accepts a value returns the credit card type
 * @param {object} data
 * @returns {object}
 */
export const getCardTypeFromMicroform = ( data ) => {
  const { microformCardType, supportedCards } = data || {};

  // Microform v2: resolve card type from hosted field change event
  if( microformCardType ){
    const cardNameMap = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'AmericanExpress',
      discover: 'Discover',
      dinersclub: 'DinersClub',
      jcb: 'JCB',
      cup: 'UnionPay'
    };

    const mappedType = cardNameMap[microformCardType.toLowerCase()] || microformCardType;
    const matchedCard = supportedCards?.find( ( card ) => card.type.toLowerCase() === mappedType.toLowerCase() );

    if( matchedCard ){
      return decorateCardType( { cardType: matchedCard } );
    }
  }

  return {};
};

/**
 * getCVVLength utility to determine required security code length
 * @param {object} microformData - onChange data from Microform SDK
 * @param {object} props - Component props (fallback)
 * @returns {number} 3 or 4
 */
export const getCVVLength = ( microformData, props ) => {

  // 1. SDK interaction (active typing)
  const sdkLength = microformData?.card?.[0]?.securityCode?.length;
  if( sdkLength ){
    return sdkLength;
  }

  // 2. Saved card brand check (initial load / masked state)
  const isAmex = ( props?.cardTypeImage === ccConstants.CCNUMBER.AMEX ) ||
                 ( props?.savedCardType === ccConstants.CCNUMBER.AMEX );

  // 3. Standard default (3) unless we are certain it is Amex (4)
  return isAmex ? 4 : 3;
};

/**
 * validateForm function to validate CVV and Expiration Date
 * @param {object} data
 * @returns {object} errors
 */
export const validateForm = ( data ) => {
  const { values, cardType, ccNumberEmpty, cvvEmpty, ccNumberValid, cvvValid, isCvvVisible, isCcNumberVisible, isCVVApplied, isEditingNumber, isEditingCvv, creditCardNumber, expirationDate, cvv } = data || {};

  const errors = {};

  if( isCcNumberVisible && ( !isCVVApplied || isEditingNumber ) ){
    if( ccNumberEmpty ){
      errors.creditCardNumber = 'Required';
    }
    else if( !ccNumberValid ){
      errors.creditCardNumber = creditCardNumber?.validations?.[0]?.message;
    }
  }

  if( isCvvVisible && ( !isCVVApplied || isEditingCvv ) ){
    if( cvvEmpty ){
      errors.cvv = 'Required';
    }
    else if( !cvvValid ){
      errors.cvv = cvv?.validations?.[0]?.message;
    }
  }

  const hasEnteredNumber = !ccNumberEmpty;

  if( !cardType?.type && hasEnteredNumber ){
    errors.creditCardNumber = creditCardNumber?.validations?.[0]?.message;
  }

  if( !values?.expirationDate ){
    return errors;
  }

  const presentDate = new Date();
  const presentYear = presentDate.getFullYear();
  const presentMonth = presentDate.getMonth() + 1;
  const expiryMonth = Number( values?.expirationDate.split( '/' )[0] );
  const expiryYear = Number(
    `${String( presentYear ).slice( 0, 2 )}${values?.expirationDate.split( '/' )[1]}`
  );

  if(
    isNaN( expiryMonth ) ||
    isNaN( expiryYear ) ||
    expiryMonth < 1 ||
    expiryMonth > 12 ||
    expiryYear < presentYear ||
    ( expiryYear === presentYear && expiryMonth < presentMonth )
  ){
    errors.expirationDate = expirationDate?.validations?.[0]?.message;
  }

  return errors;
};
